import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Icon } from '@/components/ui/icon'
import { Skeleton } from '@/components/ui/skeleton'
import { useDestinations } from '@/hooks/queries/shell'
import { THIS_DESTINATION } from '@/lib/auth/shell'

/**
 * The other VentaWiz front ends — User Management, the admin portal — as
 * VentaWiz Auth's operators list them (`GET /v1/destinations`), so a new
 * app's tile appears here with no redeploy of this one.
 *
 * Plain links, each a full page load: every destination is its own bundle
 * on this origin. The list says nothing about who may enter each; the
 * destination itself decides, as it does when anybody follows a link.
 */
export function AppsMenu() {
  const { t } = useTranslation()
  const destinations = useDestinations()
  const others = (destinations.data ?? []).filter(
    (destination) => destination.key !== THIS_DESTINATION,
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('shell.appsLabel')}>
          <Icon name="apps" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>{t('shell.apps')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {destinations.isPending ? (
          <div aria-busy="true" className="space-y-2 p-2">
            <span className="sr-only">{t('common.loading')}</span>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : destinations.isError ? (
          <p role="status" className="text-muted-foreground p-2 text-sm">
            {t('shell.appsUnavailable')}
          </p>
        ) : others.length === 0 ? (
          <p role="status" className="text-muted-foreground p-2 text-sm">
            {t('shell.appsNone')}
          </p>
        ) : (
          others.map((destination) => (
            <DropdownMenuItem key={destination.key} asChild>
              <a
                href={destination.url}
                // A name and a description: the item's one-line height would overlap them.
                className="flex h-auto flex-col items-start gap-0.5 py-2"
              >
                <span className="text-sm font-medium">{destination.name}</span>
                {destination.description ? (
                  <span className="text-muted-foreground text-xs">{destination.description}</span>
                ) : null}
              </a>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
